import {
  FETCH_LOCATIONS,
  SCAN_ITEM,
  FETCH_STOCK_LEVELS,
  FETCH_STOCK_ITEMS,
  PROCESS_SHIPMENT,
  FETCH_ALERTS,
  RESOLVE_ALERT,
  CHECK_EXPIRING,
  FETCH_CONSUMPTION_HISTORY,
  FETCH_CONSUMPTION_REPORT,
  GENERATE_DAILY_SUMMARY,
  ADJUST_STOCK,
  SET_FIFO_WARNING,
  CLEAR_FIFO_WARNING,
  SET_SCAN_MODE,
  CLEAR_SCAN_RESULT,
} from "./types";

const INITIAL_STATE = {
  // Locations
  locations: {
    list: [],
    error: null,
    loading: false,
  },

  // Scanning state
  scanning: {
    isScanning: false,
    lastScanResult: null,
    fifoWarning: null,
    error: null,
    loading: false,
    scanMode: 'camera', // 'camera' or 'manual'
  },

  // Stock levels
  stockLevels: {
    location: null,
    levels: [],
    totalItems: 0,
    lowStockItems: 0,
    error: null,
    loading: false,
    lastUpdated: null,
  },

  // Stock items (individual)
  stockItems: {
    items: [],
    hasMore: false,
    error: null,
    loading: false,
  },

  // Alerts
  alerts: {
    active: [],
    resolved: [],
    unreadCount: 0,
    error: null,
    loading: false,
  },

  // Consumption
  consumption: {
    history: [],
    report: null,
    dailySummary: null,
    hasMore: false,
    error: null,
    loading: false,
  },

  // Shipment processing
  shipmentProcessing: {
    result: null,
    error: null,
    loading: false,
  },

  // Stock adjustment
  stockAdjustment: {
    result: null,
    error: null,
    loading: false,
  },
};

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    // --------------------------------------------------------------------------
    // FETCH_LOCATIONS
    // --------------------------------------------------------------------------
    case FETCH_LOCATIONS.init:
      return {
        ...state,
        locations: { ...state.locations, error: null, loading: true },
      };
    case FETCH_LOCATIONS.success:
      // Handle both paginated (results array) and direct array responses
      const locationsList = Array.isArray(action.payload)
        ? action.payload
        : (action.payload.results || []);
      return {
        ...state,
        locations: {
          list: locationsList,
          error: null,
          loading: false,
        },
      };
    case FETCH_LOCATIONS.failure:
      return {
        ...state,
        locations: { ...state.locations, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // SCAN_ITEM
    // --------------------------------------------------------------------------
    case SCAN_ITEM.init:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          error: null,
          loading: true,
          lastScanResult: null,
          fifoWarning: null,
        },
      };
    case SCAN_ITEM.success:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          lastScanResult: action.payload,
          fifoWarning: null,
          error: null,
          loading: false,
        },
      };
    case SCAN_ITEM.failure:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          error: action.payload,
          loading: false,
          lastScanResult: null,
          fifoWarning: null,
        },
      };

    // --------------------------------------------------------------------------
    // FETCH_STOCK_LEVELS
    // --------------------------------------------------------------------------
    case FETCH_STOCK_LEVELS.init:
      return {
        ...state,
        stockLevels: { ...state.stockLevels, error: null, loading: true },
      };
    case FETCH_STOCK_LEVELS.success:
      // Transform API response to match component expectations
      const stockSummary = action.payload.summary || [];
      const transformedLevels = stockSummary.map(item => ({
        item: item.item__name,
        itemId: item.item__id,
        total_quantity: item.total_quantity,
        unit: item.item__measure_unit_display || item.item__measure_unit || 'units',
        batches: item.batch_count,
        oldest_batch: item.oldest_batch_date,
        newest_batch: item.newest_batch_date,
        // Determine status based on quantity (can be adjusted based on business logic)
        status: item.total_quantity === 0 ? 'OUT_OF_STOCK' :
                item.total_quantity < 10 ? 'LOW_STOCK' : 'NORMAL',
      }));
      const lowStockCount = transformedLevels.filter(
        item => item.status === 'LOW_STOCK' || item.status === 'OUT_OF_STOCK'
      ).length;
      return {
        ...state,
        stockLevels: {
          location: action.payload.location || null,
          levels: transformedLevels,
          totalItems: action.payload.total_items || stockSummary.length,
          lowStockItems: lowStockCount,
          error: null,
          loading: false,
          lastUpdated: action.payload.generated_at || new Date().toISOString(),
        },
      };
    case FETCH_STOCK_LEVELS.failure:
      return {
        ...state,
        stockLevels: { ...state.stockLevels, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // FETCH_STOCK_ITEMS
    // --------------------------------------------------------------------------
    case FETCH_STOCK_ITEMS.init:
      return {
        ...state,
        stockItems: { ...state.stockItems, error: null, loading: true },
      };
    case FETCH_STOCK_ITEMS.success:
      return {
        ...state,
        stockItems: {
          items: action.payload.results,
          hasMore: action.payload.next !== null,
          error: null,
          loading: false,
        },
      };
    case FETCH_STOCK_ITEMS.failure:
      return {
        ...state,
        stockItems: { ...state.stockItems, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // PROCESS_SHIPMENT
    // --------------------------------------------------------------------------
    case PROCESS_SHIPMENT.init:
      return {
        ...state,
        shipmentProcessing: { ...state.shipmentProcessing, error: null, loading: true },
      };
    case PROCESS_SHIPMENT.success:
      return {
        ...state,
        shipmentProcessing: {
          result: action.payload,
          error: null,
          loading: false,
        },
      };
    case PROCESS_SHIPMENT.failure:
      return {
        ...state,
        shipmentProcessing: {
          ...state.shipmentProcessing,
          error: action.payload,
          loading: false,
          result: null,
        },
      };

    // --------------------------------------------------------------------------
    // FETCH_ALERTS
    // --------------------------------------------------------------------------
    case FETCH_ALERTS.init:
      return {
        ...state,
        alerts: { ...state.alerts, error: null, loading: true },
      };
    case FETCH_ALERTS.success:
      // Handle both array and paginated response
      const alertsData = Array.isArray(action.payload)
        ? action.payload
        : (action.payload.results || []);
      const activeAlerts = alertsData.filter(alert => alert.is_active);
      const resolvedAlerts = alertsData.filter(alert => !alert.is_active);
      return {
        ...state,
        alerts: {
          active: activeAlerts,
          resolved: resolvedAlerts,
          unreadCount: activeAlerts.length,
          error: null,
          loading: false,
        },
      };
    case FETCH_ALERTS.failure:
      return {
        ...state,
        alerts: { ...state.alerts, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // RESOLVE_ALERT
    // --------------------------------------------------------------------------
    case RESOLVE_ALERT.init:
      return {
        ...state,
        alerts: { ...state.alerts, loading: true },
      };
    case RESOLVE_ALERT.success:
      // API returns { message, alert } - extract the alert object
      const resolvedAlert = action.payload.alert || action.payload;
      const updatedActive = state.alerts.active.filter(alert => alert.id !== resolvedAlert.id);
      return {
        ...state,
        alerts: {
          ...state.alerts,
          active: updatedActive,
          resolved: [...state.alerts.resolved, resolvedAlert],
          unreadCount: updatedActive.length,
          loading: false,
        },
      };
    case RESOLVE_ALERT.failure:
      return {
        ...state,
        alerts: { ...state.alerts, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // CHECK_EXPIRING
    // --------------------------------------------------------------------------
    case CHECK_EXPIRING.init:
      return state;
    case CHECK_EXPIRING.success:
      return state; // Alerts will be fetched separately
    case CHECK_EXPIRING.failure:
      return state;

    // --------------------------------------------------------------------------
    // FETCH_CONSUMPTION_HISTORY
    // --------------------------------------------------------------------------
    case FETCH_CONSUMPTION_HISTORY.init:
      return {
        ...state,
        consumption: { ...state.consumption, error: null, loading: true },
      };
    case FETCH_CONSUMPTION_HISTORY.success:
      // Handle both array and paginated response
      const historyData = Array.isArray(action.payload)
        ? action.payload
        : (action.payload.results || []);
      return {
        ...state,
        consumption: {
          ...state.consumption,
          history: historyData,
          hasMore: action.payload.next !== null,
          error: null,
          loading: false,
        },
      };
    case FETCH_CONSUMPTION_HISTORY.failure:
      return {
        ...state,
        consumption: { ...state.consumption, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // FETCH_CONSUMPTION_REPORT
    // --------------------------------------------------------------------------
    case FETCH_CONSUMPTION_REPORT.init:
      return {
        ...state,
        consumption: { ...state.consumption, error: null, loading: true },
      };
    case FETCH_CONSUMPTION_REPORT.success:
      return {
        ...state,
        consumption: {
          ...state.consumption,
          report: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_CONSUMPTION_REPORT.failure:
      return {
        ...state,
        consumption: { ...state.consumption, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // GENERATE_DAILY_SUMMARY
    // --------------------------------------------------------------------------
    case GENERATE_DAILY_SUMMARY.init:
      return {
        ...state,
        consumption: { ...state.consumption, loading: true },
      };
    case GENERATE_DAILY_SUMMARY.success:
      return {
        ...state,
        consumption: {
          ...state.consumption,
          dailySummary: action.payload,
          loading: false,
        },
      };
    case GENERATE_DAILY_SUMMARY.failure:
      return {
        ...state,
        consumption: { ...state.consumption, error: action.payload, loading: false },
      };

    // --------------------------------------------------------------------------
    // ADJUST_STOCK
    // --------------------------------------------------------------------------
    case ADJUST_STOCK.init:
      return {
        ...state,
        stockAdjustment: { ...state.stockAdjustment, error: null, loading: true },
      };
    case ADJUST_STOCK.success:
      return {
        ...state,
        stockAdjustment: {
          result: action.payload,
          error: null,
          loading: false,
        },
      };
    case ADJUST_STOCK.failure:
      return {
        ...state,
        stockAdjustment: {
          ...state.stockAdjustment,
          error: action.payload,
          loading: false,
          result: null,
        },
      };

    // --------------------------------------------------------------------------
    // UI STATE ACTIONS
    // --------------------------------------------------------------------------
    case SET_FIFO_WARNING:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          fifoWarning: action.payload,
        },
      };

    case CLEAR_FIFO_WARNING:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          fifoWarning: null,
        },
      };

    case SET_SCAN_MODE:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          scanMode: action.payload,
        },
      };

    case CLEAR_SCAN_RESULT:
      return {
        ...state,
        scanning: {
          ...state.scanning,
          lastScanResult: null,
          fifoWarning: null,
          error: null,
        },
      };

    default:
      return state;
  }
};